import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalService, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzTbodyComponent, NzTableModule } from 'ng-zorro-antd/table';
import { TITLE_TABLE_SIZE } from 'src/app/core/constants/style';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { PrinterOutline } from '@ant-design/icons-angular/icons';
import { IconDefinition } from '@ant-design/icons-angular';
import { NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import _ from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { PackagingsService } from 'src/app/private/services/packagings.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { ConfigurationService } from 'src/app/private/services/configuration.service';
import { ConfigurationKeysEnum } from 'src/app/shared/enums/configuration-keys.enum';

const icons: IconDefinition[] = [
    PrinterOutline
];

@Component({
    selector: 'app-modal-print-stickers',
    standalone: true,
    imports: [
        NzModalTitleDirective,
        NzTableComponent,
        NzTheadComponent,
        NzTrDirective,
        NzTableCellDirective,
        NzThMeasureDirective,
        NzTbodyComponent,
        NzModalFooterDirective,
        NzEmptyModule,
        NzIconModule,
        NzToolTipModule,
        NzTableModule,
        NzInputDirective,
        NzInputGroupComponent,
        FormsModule,
        ReactiveFormsModule,
        LoaderComponent
    ],
    templateUrl: './modal-print-stickers.component.html',
    styleUrl: './modal-print-stickers.component.scss'
})
export class ModalPrintStickersComponent implements OnInit, AfterViewInit {
	@ViewChild('modalContent') private readonly _modalContent!: ElementRef;

	isLoading: boolean = true;
	tableHeight!: number;
	stickers: PackagingDto[] = [];
	stickersFiltered: PackagingDto[] = [];
	processId: number = 0;
	processDescription: string = '';
	checked = false;
	indeterminate = false;
	setOfCheckedId = new Set<number>();
	searchPackaging: string = '';

	public data = inject(NZ_MODAL_DATA);
	private readonly modalService = inject(NzModalService);
	private readonly iconService = inject(NzIconService);
	private readonly packagingsService = inject(PackagingsService);
	private readonly toast = inject(NzMessageService);
	private readonly configurationService = inject(ConfigurationService);

	palletLotSource: 'externalBatchNumber' | 'externalId' | 'batchNumber' = 'externalBatchNumber';

	constructor() {
	    this.iconService.addIcon(...icons);
	    this.processId = this.data?.data?.processId as number;
	    this.processDescription = this.data?.data?.processDescription as string;
	}

	async ngOnInit() {
	    try {
	        this.stickers = await this.packagingsService.getPackagingsByProcessId(this.processId);
	    } catch (_error) {
	        this.stickers = this.data?.data?.packagings ?? [];
	    }
	    this.stickersFiltered = _.cloneDeep(this.stickers);
	    this.isLoading = false;

	    await this.loadPalletLotSourceConfiguration();

	    // Scroll after data is loaded and the DOM has re-rendered
	    setTimeout(() => this.scrollToLastPrinted(), 100);
	}

	async ngAfterViewInit(): Promise<void> {
	    setTimeout(() => {
	        this.tableHeight =
				(this._modalContent.nativeElement as HTMLImageElement)
				    .clientHeight - TITLE_TABLE_SIZE;
	    });
	}

	scrollToLastPrinted() {
	    const printedStickers = this.stickers.filter(s => s.labelPrinted);

	    if (printedStickers.length > 0) {
	        const lastPrinted = printedStickers.sort((a, b) => (b.id || 0) - (a.id || 0))[0];

	        if (lastPrinted?.id) {
	            setTimeout(() => {
	                const element = document.getElementById(`packaging-${lastPrinted.id}`);
	                if (element) {
	                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
	                }
	            }, 100);
	        }
	    }
	}

	handleCancel(): void {
	    this.modalService.closeAll();
	}

	printSelected() {
	    const packagingIds = [...this.setOfCheckedId];
	    this.packagingsService.sendToPrinter(packagingIds, this.processId);
	    this.toast.info(`Imprimiendo etiquetas`);
	    this.modalService.closeAll();
	}

	printSticker(id: number) {
	    this.packagingsService.sendToPrinter([id], this.processId);
	    this.toast.info(`Imprimiendo etiqueta`);
	    this.modalService.closeAll();
	}

	onAllChecked(checked: boolean): void {
	    this.stickersFiltered
	        .forEach(({ id }) => this.updateCheckedSet(id!, checked));
	    this.refreshCheckedStatus();
	}

	updateCheckedSet(id: number, checked: boolean): void {
	    if (checked) {
	        this.setOfCheckedId.add(id);
	    } else {
	        this.setOfCheckedId.delete(id);
	    }
	}

	onItemChecked(id: number, checked: boolean): void {
	    this.updateCheckedSet(id, checked);
	    this.refreshCheckedStatus();
	}

	refreshCheckedStatus(): void {
	    const listOfEnabledData = this.stickers;
	    this.checked = listOfEnabledData.every(({ id }) => this.setOfCheckedId.has(id!));
	    this.indeterminate = listOfEnabledData.some(({ id }) => this.setOfCheckedId.has(id!)) && !this.checked;
	}

	onSearch(event: string) {
	    try {
	        this.stickersFiltered = this.stickers.filter(packaging => packaging.batchNumber?.toLowerCase().includes(event.toLowerCase()));
	    } catch (error) {
	        this.toast.error(`Error al filtrar los formatos`);
	    }
	}

	async clearFilter(): Promise<void> {
	    this.searchPackaging = '';
	    this.stickersFiltered = this.stickers;
	}

	private async loadPalletLotSourceConfiguration(): Promise<void> {
	    try {
	        const configuration = await this.configurationService.getConfigurationByKey(
	            ConfigurationKeysEnum.PRODUCTION_ORDERS_REPRINT_LABEL_PALLET_LOT_SOURCE,
	        );
	        const value = (configuration?.value ?? '').toString().trim();

	        if (value === 'externalBatchNumber' || value === 'externalId' || value === 'batchNumber') {
	            this.palletLotSource = value;
	        }
	    } catch (_error) {
	        this.palletLotSource = 'externalBatchNumber';
	    }
	}

	getPalletLotValue(pallet: PackagingDto): string {
	    if (this.palletLotSource === 'externalId') {
	        return pallet.externalId?.toString() ?? pallet.externalBatchNumber ?? pallet.batchNumber ?? '-';
	    }

	    if (this.palletLotSource === 'batchNumber') {
	        return pallet.batchNumber ?? pallet.externalBatchNumber ?? pallet.externalId?.toString() ?? '-';
	    }

	    return pallet.externalBatchNumber ?? pallet.batchNumber ?? pallet.externalId?.toString() ?? '-';
	}

}

